import { SortingFn } from '@tanstack/react-table'

type SortFn = (a: any, b: any) => number

const reSplitAlphaNumeric = /([0-9]+)/gm
const alphanumeric: SortFn = (a, b) => {
  return compareAlphanumeric(toString(a).toLowerCase(), toString(b).toLowerCase())
}
const alphanumericCaseSensitive: SortFn = (a, b) => {
  return compareAlphanumeric(toString(a), toString(b))
}

const sortByArray = <TData = unknown>(arr: string[]): SortingFn<TData> => {
  return (aRow, bRow, columnId) => {
    const aVal = aRow.getValue(columnId)
    const bVal = bRow.getValue(columnId)
    const a = arr.indexOf(String(aVal))
    const b = arr.indexOf(String(bVal))
    const aAdjusted = a > -1 ? a : arr.length
    const bAdjusted = b > -1 ? b : arr.length
    return aAdjusted - bAdjusted
  }
}

// The text filter is more basic (less numeric support)
// but is much faster
const text: SortFn = (a, b) => {
  return compareBasic(toString(a).toLowerCase(), toString(b).toLowerCase())
}

// The text filter is more basic (less numeric support)
// but is much faster
const textCaseSensitive: SortFn = (a, b) => {
  return compareBasic(toString(a), toString(b))
}

const datetime: SortFn = (a, b) => {
  // Can handle nullish values
  // Use > and < because == (and ===) doesn't work with
  // Date objects (would require calling getTime()).
  return a > b ? 1 : a < b ? -1 : 0
}
const basic: SortFn = (a, b) => {
  return compareBasic(a, b)
}

// Utils

function compareBasic (a: any, b: any): number {
  return a === b ? 0 : a > b ? 1 : -1
}
function toString (a: any): string {
  if (typeof a === 'number') {
    if (isNaN(a) || a === Infinity || a === -Infinity) {
      return ''
    }
    return String(a)
  }
  if (typeof a === 'string') {
    return a
  }
  return ''
}

// Mixed sorting is slow, but very inclusive of many edge cases.
// It handles numbers, mixed alphanumeric combinations, and even
// null, undefined, and Infinity
function compareAlphanumeric (aStr: string, bStr: string): number {
  // Split on number groups, but keep the delimiter
  // Then remove falsey split values
  const a = aStr.split(reSplitAlphaNumeric).filter(Boolean)
  const b = bStr.split(reSplitAlphaNumeric).filter(Boolean)

  // While
  while (a.length > 0 && b.length > 0) {
    const aa = toString(a.shift())
    const bb = toString(b.shift())
    const an = parseInt(aa, 10)
    const bn = parseInt(bb, 10)
    const combo = [an, bn].sort((a, b) => a - b)

    // Both are string
    if (isNaN(combo[0])) {
      if (aa > bb) {
        return 1
      }
      if (bb > aa) {
        return -1
      }
      continue
    }

    // One is a string, one is a number
    if (isNaN(combo[1])) {
      return isNaN(an) ? -1 : 1
    }

    // Both are numbers
    if (an > bn) {
      return 1
    }
    if (bn > an) {
      return -1
    }
  }
  return a.length - b.length
}

// Exports

const sortingFns = {
  alphanumeric,
  alphanumericCaseSensitive,
  text,
  textCaseSensitive,
  datetime,
  basic,
  sortByArray
}

export default sortingFns
